import React from "react";
import { ChevronLeft } from "lucide-react";
import { ClientAwareLink } from "@/components/ClientAwareLink";
import { useClient } from "@/contexts/ClientContext";
import { useLocation } from "wouter";

interface SidebarProps {
  sidebarCollapsed: boolean;
  setSidebarCollapsed: (collapsed: boolean) => void;
}


const Sidebar: React.FC<SidebarProps> = ({ sidebarCollapsed, setSidebarCollapsed }) => {
  const { selectedClient } = useClient();
  const [location] = useLocation();

  const isStabilize =
    location.startsWith("/stabilize") ||
    /^\/clients\/[^/]+\/stabilize/.test(location);

  const stabilizeLinks = [
    { href: "/stabilize/load", label: "Load Test", icon: "📊" },
    { href: "/stabilize/endurance", label: "Endurance Test", icon: "🔋" },
    { href: "/stabilize/spike", label: "Spike Test", icon: "📈" },
    { href: "/stabilize/stress", label: "Stress Test", icon: "🔥" },
    { href: "/stabilize/analysis", label: "Analysis", icon: "🔍" },
  ];

  const mainLinks = [
    { href: "/dashboard", label: "Dashboard", icon: "🏠" },
    { href: "/workspace", label: "Workspace", icon: "✨" },
    { href: "/plan", label: "Plan", icon: "📋" },
    { href: "/code", label: "Code", icon: "💻" },
    { href: "/build", label: "Build", icon: "🏗️" },
    { href: "/sql/management", label: "SQL Management", icon: "🗄️" },
  ];

  const extraLinks = [
    { href: "/diagnostics", label: "Diagnostics", icon: "🩺" },
    { href: "/vm-profile", label: "VM Profile", icon: "🖥️" },
    { href: "/wsr", label: "Weekly Status", icon: "📅" },
  ];

  return (
    <aside
      className={`bg-[#f7f7f5] border-r border-[#ececeb] w-64 flex-shrink-0 h-[calc(100vh-74px)] mt-[74px] transition-all duration-300 ${sidebarCollapsed ? '-ml-64' : ''} flex flex-col`}
    >
      <div className="flex-1 overflow-y-auto custom-scrollbar p-3">
        {/* Workspace Branding */}
        <div className="flex items-center gap-2 px-2 py-3 mb-2 group cursor-pointer hover:bg-[#ebebeb] rounded-md transition-colors">
          <div className="w-6 h-6 bg-shiftleft-600 rounded flex items-center justify-center text-white font-bold text-sm">S</div>
          <span className="font-semibold text-[14px] text-gray-900">ShiftLeft Digital</span>
        </div>

        {/* Search Mockup */}
        <div className="px-2 mb-4">
          <div className="flex items-center gap-2 px-2 py-1.5 bg-white border border-[#ececeb] rounded-md text-gray-400 text-sm">
            <span>🔍</span>
            <span>Search</span>
          </div>
        </div>

        {/* Global Navigation */}
        <div className="mb-6">
          <div className="text-[11px] font-bold text-gray-400 uppercase tracking-wider px-2 mb-1">Main</div>
          <nav className="space-y-0.5">
            {mainLinks.map(link => (
              <ClientAwareLink key={link.href} href={link.href}>
                <div
                  className={`flex items-center gap-3 px-2 py-1.5 rounded-md cursor-pointer transition-colors group ${location === link.href ? "bg-[#ebebeb] text-gray-900" : "hover:bg-[#ebebeb] text-gray-600"
                    }`}
                >
                  <span className="w-5 h-5 flex items-center justify-center grayscale group-hover:grayscale-0">{link.icon}</span>
                  <span className={`text-[13px] ${location === link.href ? "font-medium" : ""}`}>{link.label}</span>
                </div>
              </ClientAwareLink>
            ))}
          </nav>
        </div>

        {/* Dynamic Context Section */}
        <div className="mb-6">
          <div className="text-[11px] font-bold text-gray-400 uppercase tracking-wider px-2 mb-1">
            {isStabilize ? "Stabilize" : "Explore"}
          </div>
          <nav className="space-y-0.5">
            {(isStabilize ? stabilizeLinks : extraLinks).map(link => (
              <ClientAwareLink key={link.href} href={link.href}>
                <div
                  className={`flex items-center gap-3 px-2 py-1.5 rounded-md cursor-pointer transition-colors group ${location === link.href ? "bg-[#ebebeb] text-gray-900" : "hover:bg-[#ebebeb] text-gray-600"
                    }`}
                >
                  <span className="w-5 h-5 flex items-center justify-center grayscale group-hover:grayscale-0">{link.icon}</span>
                  <span className={`text-[13px] ${location === link.href ? "font-medium" : ""}`}>{link.label}</span>
                </div>
              </ClientAwareLink>
            ))}
          </nav>
        </div>

        {/* Favorites Mock Section */}
        <div className="mb-6">
          <div className="text-[11px] font-bold text-gray-400 uppercase tracking-wider px-2 mb-1">Favorites</div>
          <div className="space-y-0.5 text-[13px] text-gray-500">
            <div className="px-2 py-1.5 rounded-md hover:bg-[#ebebeb] cursor-pointer flex items-center gap-3">
              <span>⭐️</span>
              <span>Active Discovery</span>
            </div>
            <div className="px-2 py-1.5 rounded-md hover:bg-[#ebebeb] cursor-pointer flex items-center gap-3">
              <span>⭐️</span>
              <span>SQL Performance</span>
            </div>
          </div>
        </div>
      </div>

      {/* Sidebar Collapse Toggle at Bottom */}
      <div className="p-3 border-t border-[#ececeb]">
        <button
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          className="w-full flex items-center gap-2 px-2 py-1.5 text-gray-400 hover:text-gray-900 transition-colors text-sm"
        >
          <ChevronLeft className={`w-4 h-4 transition-transform ${sidebarCollapsed ? 'rotate-180' : ''}`} />
          <span>Collapse</span>
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
